import React, { useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Plant, Storefront, ArrowRight, UploadSimple, ShieldCheck, FileText } from "@phosphor-icons/react";
import { API } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Onboarding() {
  const { user, setRole, checkAuth, authHeaders } = useAuth();
  const navigate = useNavigate();
  const [role, setRoleLocal] = useState("");
  const [company, setCompany] = useState("");
  const [country, setCountry] = useState("");
  const [idType, setIdType] = useState("aadhaar");
  const [idNumber, setIdNumber] = useState("");
  const [kycPath, setKycPath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef();

  if (!user) return null;
  if (user.role) {
    navigate(user.role === "farmer" ? "/farmer" : user.role === "buyer" ? "/buyer" : "/admin");
    return null;
  }

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API}/upload`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      setKycPath(r.data.path);
      toast.success("KYC document uploaded");
    } catch (e) {
      toast.error("Upload failed");
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!role) return setErr("Pick a role");
    if (role === "farmer" && (!kycPath || !idNumber)) return setErr("Farmer KYC document and ID number are required");
    setLoading(true);
    setErr("");
    try {
      const u = await setRole(role, company, country);
      if (role === "farmer") {
        await axios.post(
          `${API}/auth/kyc`,
          { kyc_path: kycPath, id_type: idType, id_number: idNumber },
          { headers: authHeaders(), withCredentials: true }
        );
        await checkAuth();
      }
      navigate(u.role === "farmer" ? "/farmer" : "/buyer");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to set role");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 hero-gradient noise" data-testid="onboarding-page">
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <p className="section-label">Welcome, {user.name?.split(" ")[0]}</p>
          <h1 className="font-display text-5xl md:text-6xl tracking-tighter mt-4">How will you use WorldFoods2U?</h1>
        </motion.div>
        <form onSubmit={submit} className="mt-14 space-y-8">
          <div className="grid md:grid-cols-2 gap-5">
            {[
              { id: "farmer", title: "I'm a Farmer", desc: "List stock, accept orders, ship to global buyers.", Icon: Plant },
              { id: "buyer", title: "I'm a Buyer", desc: "Source spices for my business. Anonymous fulfillment.", Icon: Storefront },
            ].map((r) => (
              <button
                type="button"
                key={r.id}
                onClick={() => setRoleLocal(r.id)}
                className={`text-left glass rounded-2xl p-7 border transition-all ${role === r.id ? "border-saffron ring-2 ring-saffron/30" : "border-white/10 hover:border-gold/40"}`}
                data-testid={`role-${r.id}`}
              >
                <r.Icon size={32} weight="duotone" className="text-saffron" />
                <h3 className="font-display text-2xl mt-5 text-cream">{r.title}</h3>
                <p className="text-sm text-muted mt-2 leading-relaxed">{r.desc}</p>
              </button>
            ))}
          </div>

          <div className="glass rounded-2xl p-7 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="section-label block mb-2">Company (optional)</label>
                <input
                  className="w-full bg-ink-800 border border-white/10 rounded-lg px-4 py-3 text-sm text-cream focus:border-saffron outline-none"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="e.g. Alvora Foods"
                  data-testid="company-input"
                />
              </div>
              <div>
                <label className="section-label block mb-2">Country</label>
                <input
                  className="w-full bg-ink-800 border border-white/10 rounded-lg px-4 py-3 text-sm text-cream focus:border-saffron outline-none"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder="e.g. India"
                  data-testid="country-input"
                />
              </div>
            </div>
          </div>

          {/* Farmer KYC — revealed once farmer is selected */}
          {role === "farmer" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="glass rounded-2xl p-7 space-y-5 border border-saffron/30"
              data-testid="kyc-section"
            >
              <div className="flex items-start gap-3">
                <ShieldCheck size={28} weight="duotone" className="text-saffron mt-1" />
                <div>
                  <h3 className="font-display text-2xl text-cream">Farmer KYC Verification</h3>
                  <p className="text-sm text-muted mt-1">Mandatory for farmers. Upload a clear photo of your government ID. Data is encrypted at rest.</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="section-label block mb-2">ID Type</label>
                  <select
                    className="w-full bg-ink-800 border border-white/10 rounded-lg px-4 py-3 text-sm text-cream focus:border-saffron outline-none"
                    value={idType}
                    onChange={(e) => setIdType(e.target.value)}
                    data-testid="kyc-id-type"
                  >
                    <option value="aadhaar">Aadhaar (India)</option>
                    <option value="pan">PAN (India)</option>
                    <option value="passport">Passport</option>
                    <option value="national_id">National ID</option>
                  </select>
                </div>
                <div>
                  <label className="section-label block mb-2">ID Number</label>
                  <input
                    className="w-full bg-ink-800 border border-white/10 rounded-lg px-4 py-3 text-sm text-cream focus:border-saffron outline-none"
                    value={idNumber}
                    onChange={(e) => setIdNumber(e.target.value)}
                    placeholder="xxxx-xxxx-xxxx"
                    data-testid="kyc-id-number"
                  />
                </div>
              </div>

              <div>
                <label className="section-label block mb-2">Upload ID photo</label>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={handleFile} className="hidden" />
                {!kycPath ? (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="w-full border-2 border-dashed border-white/20 hover:border-saffron rounded-xl py-8 flex flex-col items-center gap-2 transition-colors"
                    data-testid="kyc-upload-btn"
                  >
                    <UploadSimple size={28} weight="duotone" className="text-saffron" />
                    <span className="text-sm text-cream/80">{uploading ? "Uploading..." : "Click to upload ID photo"}</span>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-muted">JPG, PNG, or PDF · max 15MB</span>
                  </button>
                ) : (
                  <div className="flex items-center justify-between bg-forest/10 border border-forest/40 rounded-xl px-5 py-4" data-testid="kyc-uploaded">
                    <div className="flex items-center gap-3">
                      <FileText size={20} weight="duotone" className="text-forest" />
                      <div>
                        <div className="text-sm text-cream">KYC document uploaded</div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-muted mt-0.5">Pending review · usually 24 hours</div>
                      </div>
                    </div>
                    <button type="button" onClick={() => { setKycPath(""); fileRef.current?.click(); }} className="text-xs text-gold hover:text-saffron" data-testid="kyc-replace">
                      Replace
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {err && <p className="text-sm text-red-400">{err}</p>}
          <button type="submit" disabled={loading || !role} className="btn-primary disabled:opacity-50" data-testid="finish-onboarding">
            {loading ? "Setting up..." : "Continue"} <ArrowRight size={16} weight="bold" />
          </button>
        </form>
      </div>
    </div>
  );
}
